"use client";

import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import CircleLoader from "@/components/common/ActionLoader";
import DataTable from "@/components/DataTable";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useDebounce } from "@/hooks/useDebounce";
import { useGetExperienceUsersQuery } from "@/store/slices/apiSlice";
import { formatFullDateTime } from "@/utils/dateTimeHelpers";
import { useGetExperienceParticipationAnalyticsQuery } from "@/store/slices/apiSlice";

const PAGE_SIZE = 10;

const ExperienceUsersListDialog = ({
  open,
  onClose,
  experienceDetails,
  users,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");

  const debouncedQuery = useDebounce(query, 500);

  const experienceId = experienceDetails?._id || experienceDetails?.id;

  const {
    data: usersData,
    isLoading,
    isFetching,
  } = useGetExperienceParticipationAnalyticsQuery(experienceId, {
    skip: !experienceId || !open,
  });

  const {
    data: allUsersData,
    isLoading: isAllUsersLoading,
    isFetching: isAllUsersFetching,
  } = useGetExperienceUsersQuery(
    { experienceId },
    {
      skip: !experienceId,
    },
  );

  // TODO: Remove this when API pagination is connected.
  const tableData = useMemo(() => {
    const analytics =
      usersData?.response?.data?.participants || allUsersData?.response?.data;

    if (!analytics) return [];

    return analytics.map((item) => ({
      id: item.userId,
      name: item.name || "-",
      email: item.email || "-",
      lastActivity: item.lastActivity
        ? new Date(item.lastActivity).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "-",
      completionRate: item.completionRate || 0,
      attempts: item.attempts || 0,
      status: item.status || "-",
    }));
  }, [usersData]);
  // console.log("vvvvvvvvvvvvvvvvv", tableData);

  // TODO: Replace with usersData?.response?.data?.pages when API is ready.
  const totalPages = tableData.length ? 1 : 0;
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleDialogClose = () => {
    setCurrentPage(1);
    setQuery("");
    onClose?.();
  };

  const columns = [
    {
      header: "User name & Email",
      accessor: "user",
      render: (row) => (
        <div className="min-w-[220px]">
          <span className="font-semibold ">{row?.name || "-"}</span>
          <span className="">, </span>
          <span className="font-semibold text-[#67D8FF]">
            {row?.email || "-"}
          </span>
        </div>
      ),
    },
    {
      header: "Last Activity",
      accessor: "lastActivity",
      render: (row) => (
        <div className="min-w-[150px]">
          {formatFullDateTime(row?.lastActivity) || "-"}
        </div>
      ),
    },
    {
      header: "Completion Rate",
      accessor: "completionRate",
      render: (row) => {
        const completionRate = Number(row?.completionRate || 0);

        return (
          <div className="flex min-w-[170px] items-center gap-5">
            <div className="h-2 w-[86px] overflow-hidden rounded-full bg-neutral-100/15">
              <div
                className="h-full rounded-full bg-gradient-to-r from-[#20D3E6] to-[#8A3FFC]"
                style={{ width: `${completionRate}%` }}
              />
            </div>

            <span className="font-semibold ">{completionRate}%</span>
          </div>
        );
      },
    },
    {
      header: "Attempts",
      accessor: "attempts",
      render: (row) => <div className="">{row?.attempts ?? "-"}</div>,
    },
    {
      header: "Status",
      accessor: "status",
      render: (row) => (
        <div
          className={`font-medium ${
            row?.status === "Completed"
              ? "text-status-green"
              : "text-neutral-100/85"
          }`}
        >
          {row?.status || "-"}
        </div>
      ),
    },
  ];

  return (
    <Dialog open={open} onOpenChange={(value) => !value && handleDialogClose()}>
      <CircleLoader
        loading={isLoading || isFetching || isAllUsersLoading}
        size={64}
      />

      <DialogContent
        onInteractOutside={(e) => e.preventDefault()}
        className="w-[95vw] max-w-6xl rounded-lg border border-brand-11 bg-brand-19 p-0 text-neutral-100 overflow-auto"
      >
        <div className="p-8">
          <div className="mb-4 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <DialogHeader>
              <DialogTitle className="text-left text-2xl font-bold text-[#67D8FF]">
                Users participated in this Experience
              </DialogTitle>
            </DialogHeader>

            {/* <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-100/50" />

              <Input
                placeholder="Search for..."
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className="form-dark-input h-10 w-full border-[#14708A] bg-[#151515] pl-10 text-neutral-100 placeholder:text-neutral-100/50 sm:w-80"
              />
            </div> */}
          </div>

          <div className="max-h-[560px] min-h-[420px] bg-brand-19 overflow-auto">
            <DataTable
              data={tableData}
              columns={columns}
              // totalPages={totalPages}
              // currentPage={currentPage}
              // onPageChange={handlePageChange}
              isLoading={isLoading || isFetching}
              searchPlaceholder="Search for..."
              bgColor="[#172D45]"
              tableHeaderBg="[#172D45]"
              borderNone={false}
              showPagination={false}
            />
          </div>

          <DialogFooter className="flex justify-end p-4 ">
            <DialogClose asChild>
              <Button
                type="button"
                onClick={handleDialogClose}
                variant="neutral"
                className="
                  h-11
                  min-w-[110px]
                  max-w-[120px]
                "
              >
                Close
              </Button>
            </DialogClose>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExperienceUsersListDialog;
